import { useState } from "react";

import { FileRejection } from "react-dropzone";

import { useUploadFile } from "src/api/filesApi/queries";
import {
  FailureDescriptionRow,
  SIZE_FORMAT_HINT,
} from "src/cases/FailureDescriptionRow";
import { UploadFileEnvFilesPostEnvEnum } from "src/clients/files";
import {
  DEFAULT_TOAST_DURATION_MS,
  LONG_TOAST_DURATION_MS,
} from "src/design-system/Toast/constants";
import { toastActions } from "src/design-system/Toast/utils";
import { type Environment } from "src/hooks/useEnvironment";
import { pluralize } from "src/utils/stringUtils";

export const MAX_CASE_FILES = 10;

/** Every state reuses this id, so the toast is replaced in place, never stacked. */
const TOAST_ID = "case-file-upload";

const RETRY_HINT = "Upload failed for selected file(s). Please try again.";

type UploadOutcome = {
  fileIds: string[];
  /** The upload did not complete for these. Retryable. */
  failedFiles: File[];
  /** The dropzone refused these for size or format. Not retryable. */
  refusedFiles: File[];
};

const showProgress = (landed: number, total: number): void => {
  toastActions.loading({
    id: TOAST_ID,
    title:
      total === 1
        ? "File uploading"
        : `${landed} out of ${total} files uploading`,
    isDismissible: false,
  });
};

const reportFileLimit = (): void => {
  toastActions.failure({
    title: `You can upload up to ${MAX_CASE_FILES} files at once`,
  });
};

const reportOutcome = (
  { fileIds, failedFiles, refusedFiles }: UploadOutcome,
  onRetry: (files: File[]) => void,
): void => {
  const landed = fileIds.length;
  const total = landed + failedFiles.length + refusedFiles.length;

  if (failedFiles.length === 0 && refusedFiles.length === 0) {
    toastActions.success({
      id: TOAST_ID,
      title: "File(s) uploaded successfully",
      duration: DEFAULT_TOAST_DURATION_MS,
    });
    return;
  }

  // No list: every picked file shares the one problem the sentence names.
  if (landed === 0 && failedFiles.length === 0) {
    toastActions.failure({
      id: TOAST_ID,
      title: "File upload failed",
      description: SIZE_FORMAT_HINT,
      duration: LONG_TOAST_DURATION_MS,
    });
    return;
  }

  if (landed === 0 && refusedFiles.length === 0) {
    toastActions.failure({
      id: TOAST_ID,
      title: "File upload failed",
      description: RETRY_HINT,
      duration: LONG_TOAST_DURATION_MS,
      actionText: "Try again",
      onActionClick: () => onRetry(failedFiles),
    });
    return;
  }

  toastActions.multi({
    id: TOAST_ID,
    title:
      landed > 0
        ? `${landed} of ${total} files uploaded`
        : "File upload failed",
    duration: LONG_TOAST_DURATION_MS,
    items: [
      ...(landed > 0
        ? [
            {
              variant: "success" as const,
              description: `${pluralize(landed, "file")} uploaded successfully`,
            },
          ]
        : []),
      {
        variant: "error" as const,
        description: (
          <FailureDescriptionRow
            failedFiles={failedFiles}
            refusedFiles={refusedFiles}
          />
        ),
      },
    ],
  });
};

type UseCaseFilesUploadOptions = {
  env: Environment;
  /**
   * Keeps the ids that landed: the wizard in the form, the Case view on the Case.
   * A rejection means nothing reached the user.
   */
  onFilesUploaded: (fileIds: string[], categoryKey: string) => Promise<unknown>;
};

/**
 * Carries one dropzone selection to the end: the cap, the upload, keeping the
 * ids, and one toast that moves from progress to the outcome.
 */
export const useCaseFilesUpload = ({
  env,
  onFilesUploaded,
}: UseCaseFilesUploadOptions) => {
  const { onFileUpload } = useUploadFile();
  const [isUploading, setIsUploading] = useState(false);

  const uploadEnv =
    env === "sandbox"
      ? UploadFileEnvFilesPostEnvEnum.SANDBOX
      : UploadFileEnvFilesPostEnvEnum.LIVE;

  const upload = async (
    files: File[],
    refusedFiles: File[],
    categoryKey: string,
  ): Promise<void> => {
    setIsUploading(true);

    const total = files.length + refusedFiles.length;
    showProgress(0, total);

    const landed = { count: 0 };
    const results = await Promise.all(
      files.map((file) =>
        onFileUpload(file, categoryKey, uploadEnv, "case-files")
          .catch(() => undefined)
          .then((fileId) => {
            if (fileId) {
              landed.count += 1;
              // A lone file has no count in its title, so nothing to update.
              if (total > 1) showProgress(landed.count, total);
            }
            return { file, fileId };
          }),
      ),
    );

    const fileIds = results
      .map((result) => result.fileId)
      .filter((fileId): fileId is string => Boolean(fileId));
    const failedFiles = results
      .filter((result) => !result.fileId)
      .map((result) => result.file);

    // With no ids there is nothing to write. A rejection means every file that
    // uploaded still counts as failed, because none of them reached the user.
    const kept =
      fileIds.length === 0 ||
      (await onFilesUploaded(fileIds, categoryKey)
        .then(() => true)
        .catch(() => false));

    setIsUploading(false);

    reportOutcome(
      kept
        ? { fileIds, failedFiles, refusedFiles }
        : { fileIds: [], failedFiles: [...files], refusedFiles },
      (retryFiles) => void upload(retryFiles, [], categoryKey),
    );
  };

  const handleDrop = (
    acceptedFiles: File[],
    rejectedFiles: FileRejection[],
    categoryKey: string | null,
  ): void => {
    if (acceptedFiles.length + rejectedFiles.length > MAX_CASE_FILES) {
      reportFileLimit();
      return;
    }

    const refusedFiles = rejectedFiles.map((rejection) => rejection.file);

    if (acceptedFiles.length > 0 && categoryKey) {
      void upload(acceptedFiles, refusedFiles, categoryKey);
      return;
    }

    // Nothing to upload, so the toast opens straight in its outcome state.
    if (refusedFiles.length > 0) {
      reportOutcome({ fileIds: [], failedFiles: [], refusedFiles }, () => {});
    }
  };

  return { handleDrop, isUploading };
};
